"use client";

import { useTranslations } from "@i18n/react";
import { useRef, useState } from "react";

import { consoleErrorPackagesSdk } from "../utils/consoleErrorPackagesSdk";
import type { ImageUploadFailureReason } from "../utils/image-uploads/imageUploadError.error";
import {
  classifyImageUploadFailure,
  describeImageUploadFailure,
  resolveMaxUploadBytes,
} from "../utils/image-uploads/imageUploadError.error";
import {
  formatUploadSizeLabel,
  SUPPORTED_IMAGE_TYPES_LABEL,
} from "../utils/image-uploads/imageUploadLimits.constants";
import type { PresignedUpload } from "../utils/image-uploads/presignedUploadSchema";
import { uploadToPresignedUrl } from "../utils/image-uploads/uploadToPresignedUrl";

export type SelectedImage = {
  url: string;
  s3Path: string;
  originalSrc?: string;
  lastCropState?: { crop: { x: number; y: number }; zoom: number };
};

export type PresignedResult = PresignedUpload;

export type { ImageUploadFailureReason };

/**
 * Copy per failure reason. `network` and `unknown` share the generic string:
 * the browser reports a blocked request and a dropped one identically, so we
 * can't honestly tell an admin which one they hit. They stay distinct as
 * reasons because the logs and any analytics still benefit from the split.
 */
const MESSAGE_KEY_BY_REASON: Record<ImageUploadFailureReason, string> = {
  network: "events_image_upload_error",
  permission: "events_image_upload_error_permission",
  session_expired: "events_image_upload_error_session_expired",
  too_large: "events_image_upload_error_too_large",
  unsupported_type: "events_image_upload_error_unsupported_type",
  unknown: "events_image_upload_error",
};

export const useImageUpload = ({
  getPresignedUrl,
  onUploadComplete,
}: {
  getPresignedUrl: () => Promise<PresignedResult>;
  onUploadComplete: (image: SelectedImage) => void;
}) => {
  const t = useTranslations();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorReason, setErrorReason] =
    useState<ImageUploadFailureReason | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const clearError = () => {
    setError(null);
    setErrorReason(null);
  };

  const upload = async (
    blob: Blob,
    meta?: Pick<SelectedImage, "originalSrc" | "lastCropState">,
  ): Promise<SelectedImage | null> => {
    setIsUploading(true);
    clearError();
    try {
      const presigned = await getPresignedUrl();
      await uploadToPresignedUrl(presigned, blob);
      const objectUrl = URL.createObjectURL(blob);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = objectUrl;
      const image: SelectedImage = {
        url: objectUrl,
        s3Path: presigned.s3_path,
        ...meta,
      };
      onUploadComplete(image);
      return image;
    } catch (e) {
      const reason = classifyImageUploadFailure(e);
      consoleErrorPackagesSdk({
        error: describeImageUploadFailure(e),
        heading: `Image upload failed (${reason})`,
      });
      setErrorReason(reason);
      setError(
        t(MESSAGE_KEY_BY_REASON[reason], {
          max: formatUploadSizeLabel(resolveMaxUploadBytes(e)),
          types: SUPPORTED_IMAGE_TYPES_LABEL,
        }),
      );
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    upload,
    isUploading,
    error,
    errorReason,
    clearError,
  };
};
